/**
 * **Long-press** click-vs-hold disambiguation: the pure decision core behind the
 * in-world context menu's trigger (ADR-0169, replacing the shift+left-click of
 * ADR-0161). Holding the left button (or a single touch) still over an entity for
 * {@link LONG_PRESS_MS} opens the context menu; a quick press-and-release keeps its
 * normal action (rat pursuit, a duel-target pick, tap-to-walk on touch); and a press
 * that *moves* past {@link LONG_PRESS_MOVE_PX} is a drag/steer, never a menu.
 *
 * Like its siblings {@link autowalk} / {@link pinchZoom}, this holds **no DOM, no
 * Pixi and no timers**: `main.ts` owns the pointer listeners and the `setTimeout`
 * that fires the open, this owns the thresholds and the geometry so the
 * disambiguation unit-tests in node. The two constants are the single source shared
 * by the desktop mouse path and the touch tap machine, so the two schemes can never
 * disagree on what counts as a hold vs a drag.
 */

/**
 * How long the left button / a single touch must be held **still** over an entity
 * before the context menu opens, in milliseconds. Tunable: long enough that a normal
 * click never trips it, short enough to feel like a deliberate press-and-hold. Sits
 * above the touch tap-commit window (`TAP_MS`) so a medium hold neither walks nor
 * opens the menu rather than doing both.
 */
export const LONG_PRESS_MS = 500;

/**
 * How far (canvas pixels) the pointer may drift from the press origin and still count
 * as a stationary hold. Any drift past this abandons the pending long-press: the press
 * became a drag/steer, not a menu gesture. Matched to the tap-to-walk move tolerance so
 * the same wobble is forgiven by both.
 */
export const LONG_PRESS_MOVE_PX = 10;

/** A screen-space point (canvas pixels), structurally compatible with a `PointerEvent` reading. */
export interface Point {
  x: number;
  y: number;
}

/**
 * Whether the pointer has drifted far enough from the press origin to abandon a pending
 * long-press (i.e. the press became a drag). `> tolerance` so a press exactly at the
 * tolerance still holds; anything beyond it cancels.
 */
export function movedBeyondTolerance(
  start: Point,
  current: Point,
  tolerancePx: number = LONG_PRESS_MOVE_PX,
): boolean {
  return Math.hypot(current.x - start.x, current.y - start.y) > tolerancePx;
}
